import type { GetSuprSendUserBody } from "@nlc/shared-types";

import { useIsMutating } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Modal } from "@/components/modal";
import { Switch } from "@/components/switch";
import { notificationsKeys } from "@/hooks/queryKeys";
import { useUser } from "@/hooks/useUser";

import { useUpdateCustomProperties } from "../../hooks/useUpdateCustomProperties";
import { days } from "../utils/convertHours";
import { getTimezoneDisplayName } from "../utils/getEasternTime";
import { showToast, ToastTypeEnum } from "../utils/showToast";
import { Loading } from "./Loading";

export default function PauseNotifications({
  user,
}: {
  user: GetSuprSendUserBody;
}) {
  const update = useUpdateCustomProperties();
  const [daysOff, setDaysOff] = useState<string[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const { data } = useUser();
  const isLoading = useIsMutating({
    mutationKey: notificationsKeys.updateCustomProperties(),
  });

  useEffect(() => {
    if (user.properties.daysOff) {
      const parsedDaysOff = JSON.parse(user.properties.daysOff);

      if (JSON.stringify(parsedDaysOff) !== JSON.stringify(daysOff)) {
        setDaysOff(parsedDaysOff);
      }
    }
  }, [user]);

  const updateDayOffState = (day: string) => {
    setDaysOff((prev) => {
      let prevDaysOff: string[] = [];

      prevDaysOff = prev.includes(day)
        ? prev.filter((item) => item !== day)
        : [...prev, day];

      return prevDaysOff;
    });
  };

  const handleSavePauseNotifications = () => {
    update.mutate({ key: "daysOff", value: JSON.stringify(daysOff) });

    showToast(
      "Notification preferences have been updated",
      ToastTypeEnum.SUCCESS,
    );
    setOpenModal(!openModal);
  };

  const handleClearSettings = async () => {
    update.mutate({ key: "daysOff", value: JSON.stringify([]) });

    setDaysOff([]);
    showToast(
      "Notification preferences have been updated",
      ToastTypeEnum.SUCCESS,
    );
    setOpenModal(!openModal);
  };

  const onHandleModal = () => setOpenModal(!openModal);

  return (
    <>
      <Button onPress={onHandleModal} variant="secondary">
        Pause Settings
      </Button>
      <Modal isOpen={openModal} onOpenChange={onHandleModal}>
        <div className="mt-2 p-5">
          <div className="flex justify-between">
            <h2 className="text-text-primary text-xl font-semibold">
              Pause notifications settings
            </h2>
            <X
              className="text-text-quaternary cursor-pointer"
              onClick={onHandleModal}
            />
          </div>
          <div className="space-y-3">
            {data ? (
              <Badge className="w-fit mt-2" variant="blue">
                Timezone: {getTimezoneDisplayName(data)}
              </Badge>
            ) : null}
            <p className="mt-4 text-text-primary">
              Turn off the days you want to <strong>pause</strong> the
              notifications.
            </p>
            <div className="grid grid-cols-3 gap-x-4 gap-y-3 mt-4">
              {days.map((day) => (
                <div className="flex items-center" key={day}>
                  <Switch
                    isSelected={!daysOff.includes(day)}
                    onChange={() => updateDayOffState(day)}
                  />
                  <p className="pl-4 text-text-quaternary font-medium">
                    {day.slice(0, 3)}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-row justify-between mt-8">
            <Button onPress={handleClearSettings} variant="secondary">
              Clear all
            </Button>
            <div className="flex flex-row">
              <Button
                className="mr-3"
                onPress={onHandleModal}
                variant="secondary"
              >
                Back
              </Button>
              <div>
                <Button
                  className="w-15 h-10"
                  isDisabled={!!isLoading}
                  onPress={handleSavePauseNotifications}
                  variant="primary"
                >
                  {isLoading ? <Loading /> : "Save"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}
